import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Seo } from '../components/Seo';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import Logo from '../components/Logo';
import { Loader2, CheckCircle, AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setLoading(false);
      setError('Invalid verification link. Please request a new one.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/verify-email`, { token });
      setSuccess(true);
      toast.success('Email verified successfully!');

      // Refresh AuthContext so the app immediately reflects the verified state
      // (prevents stale "Unverified" UI blocking invoice sends until hard refresh)
      try {
        await refreshUser();
      } catch (refreshErr) {
        // If user isn't logged in on this device, refreshUser will fail silently — that's ok.
        if (process.env.NODE_ENV === 'development') {
          console.warn('refreshUser after verify-email failed:', refreshErr);
        }
      }

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to verify email. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Seo noindex />
      {/* Left side - Riso Blue panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-accent text-accent-foreground p-12 flex-col justify-end">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 mb-6 block">
          ✉ &nbsp; Verification
        </span>
        <h2 className="font-heading font-semibold text-4xl mb-4">Email Verification</h2>
        <p className="opacity-90 text-lg max-w-md">
          Verifying your account to unlock all features.
        </p>
      </div>

      {/* Right side - Content */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <header className="p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Logo />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-none bg-transparent">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-3xl font-heading font-semibold">
                {loading ? 'Verifying...' : success ? 'Email Verified!' : 'Verification Failed'}
              </CardTitle>
              <CardDescription>
                {loading 
                  ? "Please wait while we verify your email"
                  : success 
                    ? "Your email has been verified successfully"
                    : error
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-8">
                {loading ? (
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                ) : success ? (
                  <>
                    <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="text-muted-foreground mb-6">
                      You can now send invoices and access all features.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Redirecting to dashboard...
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-destructive" />
                    </div>
                    <p className="text-muted-foreground mb-6">
                      {error}
                    </p>
                    <Link to="/dashboard">
                      <Button className="rounded-full">
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Verification Email
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Link 
                to="/dashboard" 
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                data-testid="go-to-dashboard-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Dashboard
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
